import {
  Alert,
  GetSubscriptionResponse,
  PostAlertsRequest,
  PostAlertsResponse,
  PostReadRequest,
  PostSubscriptionRequest,
  SourceType,
  TrackingStatus,
} from '@toggle/toggle';

import { MOCK_ALERTS } from './mock-alerts';

const MOCK_LATENCY_MS = 300;

const delay = (ms: number = MOCK_LATENCY_MS) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

let alertsState: Alert[] = structuredClone(MOCK_ALERTS);

type SubscriptionKey = `${string}:${SourceType}`;

const subscriptionsState = new Map<SubscriptionKey, TrackingStatus>();

const getSubscriptionKey = (
  sourceId: string,
  sourceType: SourceType
): SubscriptionKey => `${sourceId}:${sourceType}`;

export const postAlerts = async (
  body: PostAlertsRequest
): Promise<PostAlertsResponse> => {
  await delay();

  let filtered = [...alertsState];

  if (body.type) {
    filtered = filtered.filter(alert => alert.source_type === body.type);
  }

  if (body.read !== undefined) {
    filtered = filtered.filter(alert => alert.read === body.read);
  }

  const limit = body.limit ?? filtered.length;
  const startIndex = body.next ? parseInt(body.next, 10) : 0;
  const sliced = filtered.slice(startIndex, startIndex + limit);
  const hasMore = startIndex + limit < filtered.length;

  return {
    alerts: sliced,
    next_cursor: hasMore ? String(startIndex + limit) : undefined,
  };
};

export const getSubscription = async (
  sourceId: string,
  sourceType: SourceType
): Promise<GetSubscriptionResponse> => {
  await delay();

  const key = getSubscriptionKey(sourceId, sourceType);
  const trackingStatus = subscriptionsState.get(key) ?? 'stopped';

  return {
    source_id: sourceId,
    source_type: sourceType,
    tracking_status: trackingStatus,
  };
};

export const postSubscription = async (
  body: PostSubscriptionRequest
): Promise<void> => {
  await delay();

  const key = getSubscriptionKey(body.source_id, body.source_type);
  const newStatus = body.track ? 'active' : 'stopped';
  subscriptionsState.set(key, newStatus);

  alertsState = alertsState.map(alert =>
    alert.source_id === body.source_id && alert.source_type === body.source_type
      ? { ...alert, tracking_status: newStatus }
      : alert
  );
};

export const postRead = async (body: PostReadRequest): Promise<void> => {
  await delay();

  const idSet = new Set(body.ids);
  alertsState = alertsState.map(alert =>
    idSet.has(alert.id) ? { ...alert, read: !alert.read } : alert
  );
};

export const postReadAll = async (): Promise<void> => {
  await delay();

  alertsState = alertsState.map(alert => ({ ...alert, read: true }));
};
